import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { LongTextarea } from "@/components/LongText";
import { Plus, X, Flame, UserPlus, CalendarPlus, ScanLine, LifeBuoy, Check } from "lucide-react";
import { GiraAField } from "@/components/GiraNotizia";
import { TipologiaSelect, TIPOLOGIE_GRUPPI } from "@/lib/catalogo";
import { AcqModal } from "@/pages/Acquirenti";

const SINONIMI = { "box": "Box/Garage", "garage": "Box/Garage", "autorimessa": "Box/Garage", "posto auto": "Posto auto", "villetta": "Villetta a schiera", "schiera": "Villetta a schiera", "bifamiliare": "Bifamiliare/Trifamiliare", "trifamiliare": "Bifamiliare/Trifamiliare", "rustico": "Rustico/Casale", "casale": "Rustico/Casale", "palazzina": "Edificio intero/Palazzina", "magazzino": "Magazzino/Deposito", "b&b": "Albergo/B&B", "albergo": "Albergo/B&B", "hotel": "Albergo/B&B", "appartament": "Appartamento", "bilocale": "Appartamento", "trilocale": "Appartamento", "quadrilocale": "Appartamento", "terreno agricolo": "Terreno agricolo", "terreno edificabile": "Terreno edificabile", "lotto edificabile": "Terreno edificabile" };
const riconosciTipologia = (t) => {
  const s = ` ${(t || "").toLowerCase()} `;
  const nomi = TIPOLOGIE_GRUPPI.flatMap(([, l]) => l).filter((x) => x !== "Altro").map((x) => [x.toLowerCase(), x]);
  const hit = [...nomi, ...Object.entries(SINONIMI)].map(([k, v]) => [s.indexOf(k), v]).filter(([i]) => i >= 0).sort((a, b) => a[0] - b[0]);
  return hit.length ? hit[0][1] : null;
};

const ACTIONS = [
  { key: "notizia", label: "Notizia", icon: Flame },
  { key: "acquirente", label: "Nuovo acquirente", icon: UserPlus },
  { key: "appuntamento", label: "Nuovo appuntamento", icon: CalendarPlus },
  { key: "scansiona", label: "Scansiona documento", icon: ScanLine },
  { key: "ticket", label: "Ticket alla segreteria", icon: LifeBuoy },
];

// mapping dei campi estratti -> campi del record, per la conferma a 1 tocco
const MAP = {
  notizia: { endpoint: "/notizie/rapida", estrai: (id) => `/notizie/${id}/estrai`, put: (id) => `/notizie/${id}`, detail: (id) => `/notizie/${id}`,
    extra: ["nome", "cognome", "telefono", "email", "codice_fiscale", "tipologia"], title: "Nuova notizia",
    placeholder: "Note (facoltative)… es. 'vende il trilocale entro l'anno, il figlio si trasferisce'",
    apply: (p) => { const o = {}; if (p.proprietario) o.nome = p.proprietario; if (p.indirizzo) o.indirizzo = p.indirizzo; if (p.tipologia) o.tipologia = p.tipologia; if (p.tempistica) o.note_commerciali = p.tempistica; return o; },
    show: (p) => [p.proprietario && `Proprietario: ${p.proprietario}`, p.indirizzo && `Indirizzo: ${p.indirizzo}`, p.tipologia && `Tipologia: ${p.tipologia}`, p.tempistica && `Tempistica: ${p.tempistica}`].filter(Boolean) },
  acquirente: { endpoint: "/acquirenti/rapida", estrai: (id) => `/acquirenti/${id}/estrai`, put: (id) => `/acquirenti/${id}`, detail: (id) => `/acquirenti?open=${id}`,
    extra: ["nome", "telefono"], title: "Nuovo acquirente",
    placeholder: "Scrivi o detta… es. 'Cerca trilocale in Centro, budget 180-220 mila, almeno 80 mq'",
    apply: (p) => p,
    show: (p) => [p.zona && `Zona: ${p.zona}`, p.tipologia && `Tipologia: ${p.tipologia}`, (p.budget_min || p.budget_max) && `Budget: ${p.budget_min || "?"}–${p.budget_max || "?"}`, p.mq_min && `Mq min: ${p.mq_min}`].filter(Boolean) },
};

function useAnyModalOpen() {
  const [v, setV] = useState(false);
  useEffect(() => {
    const check = () => {
      let found = document.querySelectorAll('[role="dialog"]').length > 0;
      if (!found) {
        document.querySelectorAll("div.fixed.inset-0").forEach((el) => {
          const c = el.getAttribute("class") || "";
          if (/z-50|z-\[60\]|z-\[70\]|z-\[80\]/.test(c)) found = true;
        });
      }
      setV(found);
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return v;
}

export default function QuickFab() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const modalOpen = useAnyModalOpen();

  const pick = (k) => {
    setOpen(false);
    if (k === "notizia" || k === "acquirente") return setModal(k);
    if (k === "appuntamento") return nav("/agenda?new=1");
    if (k === "scansiona") return nav("/documenti");
    if (k === "ticket") return nav("/ticket?new=1");
  };

  return (
    <>
      {open && !modalOpen && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
      <div className={`quickfab-wrap fixed z-40 bottom-6 left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:right-8 flex-col items-center lg:items-end gap-2 ${modalOpen ? "hidden" : "flex"}`}>
        {open && (
          <div data-testid="quick-fab-menu" className="flex flex-col items-stretch gap-2 mb-1 max-w-[90vw]">
            {ACTIONS.map((a) => (
              <button key={a.key} data-testid={`quick-fab-${a.key}`} onClick={() => pick(a.key)}
                className="flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full bg-white border border-[var(--border)] shadow-lg text-sm font-semibold text-[#111111] hover:bg-slate-50 transition-colors">
                <span className="w-7 h-7 rounded-full bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center shrink-0"><a.icon size={15} /></span>
                {a.label}
              </button>
            ))}
          </div>
        )}
        <button data-testid="quick-fab-toggle" onClick={() => setOpen((o) => !o)}
          className="w-14 h-14 rounded-full bg-[#111111] text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
          {open ? <X size={24} /> : <Plus size={26} />}
        </button>
      </div>
      {modal === "acquirente" && <AcqModal onClose={() => setModal(null)} onSaved={() => { setModal(null); nav("/acquirenti"); }} />}
      {modal && modal !== "acquirente" && <QuickCreateModal kind={modal} onClose={() => setModal(null)} />}
    </>
  );
}

const PH = { nome: "Nome", cognome: "Cognome", telefono: "Telefono", email: "Email", codice_fiscale: "Codice fiscale", tipologia: "Tipologia" };

export function DuplicatiIndirizzo({ list }) {
  const [riattivate, setRiattivate] = useState({});
  if (!list || !list.length) return null;
  const riattiva = async (id) => {
    try { await api.post(`/notizie-archiviate/${id}/riattiva`); setRiattivate((r) => ({ ...r, [id]: true })); toast.success("Notizia riattivata"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="duplicati-indirizzo" className="mb-2 p-2.5 rounded-xl border border-amber-300 bg-amber-50 text-xs text-amber-800 space-y-1">
      {list.map((d, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate">Possibile doppione{d.archiviata && !riattivate[d.id] ? " (archiviata)" : ""}: {d.tipo === "immobile" ? "immobile" : "notizia"} {d.link ? <a data-testid={`dup-link-${i}`} href={d.link} className="font-bold underline">{d.titolo}</a> : <b>{d.titolo}</b>}{!d.accessibile && d.agente ? ` (di ${d.agente})` : ""}</span>
          {d.archiviata && d.id && !riattivate[d.id]
            ? <button type="button" data-testid={`dup-riattiva-${i}`} onClick={() => riattiva(d.id)} className="shrink-0 font-bold underline">riattiva</button>
            : d.link && <a data-testid={`dup-apri-${i}`} href={d.link} className="shrink-0 font-bold underline">apri</a>}
        </div>
      ))}
    </div>
  );
}

function QuickCreateModal({ kind, onClose }) {
  const nav = useNavigate();
  const cfg = MAP[kind];
  const [testo, setTesto] = useState("");
  const [extra, setExtra] = useState({});
  const [saving, setSaving] = useState(false);
  const [rec, setRec] = useState(null);      // record creato
  const [proposta, setProposta] = useState(null); // campi estratti
  const isN = kind === "notizia";
  const [via, setVia] = useState("");
  const [civico, setCivico] = useState("");
  const viaCompleta = [via.trim(), civico.trim()].filter(Boolean).join(" ");
  const [comune, setComune] = useState("Reggio Emilia");
  const [dupl, setDupl] = useState([]);
  const [altri, setAltri] = useState(false);
  const [gira, setGira] = useState({ a: "", note: "" });
  const checkVia = () => { if (isN && via.trim()) api.get("/notizie-check-indirizzo", { params: { indirizzo: viaCompleta, comune } }).then((r) => setDupl(r.data.duplicati || [])).catch(() => {}); };

  const salva = async () => {
    if (isN && !via.trim()) return toast.error("Inserisci la via");
    if (isN && !comune.trim()) return toast.error("Inserisci il comune");
    let ex = extra;
    if (isN && !extra.tipologia) {
      const trovata = riconosciTipologia(testo);
      if (trovata) { ex = { ...extra, tipologia: trovata }; setExtra(ex); toast.message(`Tipologia riconosciuta: ${trovata}`); }
    }
    if (!isN && !testo.trim()) return toast.error("Scrivi (o detta) il testo");
    setSaving(true);
    try {
      const r = await api.post(cfg.endpoint, isN ? { indirizzo: viaCompleta, comune, testo, ...ex, ...(gira.a ? { gira_a: gira.a, note_girata: gira.note } : {}) } : { testo, ...extra });
      const id = r.data?.id;
      setRec(r.data);
      toast.success("Salvato");
      // Notizia: estrazione in BACKGROUND lato server → vai alla scheda, che si aggiorna da sola.
      if (kind === "notizia") { if (gira.a) { window.dispatchEvent(new Event("notizia-girata")); toast.success("Notizia girata"); onClose(); return; } onClose(); nav(cfg.detail(id)); return; }
      // Acquirente: estrazione sincrona best-effort (non blocca mai).
      try {
        const e = await api.post(cfg.estrai(id));
        const fields = e.data?.fields || {};
        if (e.data?.ok && Object.keys(fields).length) { setProposta(fields); return; }
        if (e.data && e.data.ok === false) toast.message("Compilazione automatica non riuscita, completa a mano");
      } catch { toast.message("Compilazione automatica non riuscita, completa a mano"); }
      onClose(); nav(cfg.detail(id));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const conferma = async () => {
    setSaving(true);
    const payload = cfg.apply(proposta);
    if (kind === "notizia") payload.estrazione_stato = "confermata";
    try { await api.put(cfg.put(rec.id), payload); toast.success("Campi applicati"); }
    catch { /* la notizia resta salvata comunque */ }
    finally { setSaving(false); onClose(); nav(cfg.detail(rec.id)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={`quick-${kind}-modal`}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg card-immo p-5 rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">{cfg.title}</h3>
          <button onClick={onClose} data-testid={`quick-${kind}-close`} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>

        {!proposta ? (
          <>
            {isN && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                <input data-testid="quick-notizia-via" value={via} onChange={(e) => setVia(e.target.value)} onBlur={checkVia} autoFocus
                  placeholder="Via *" className="col-span-3 w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base" />
                <input data-testid="quick-notizia-civico" value={civico} onChange={(e) => setCivico(e.target.value)} onBlur={checkVia}
                  placeholder="Civico" className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base" />
                <input data-testid="quick-notizia-comune" value={comune} onChange={(e) => setComune(e.target.value)} onBlur={checkVia}
                  placeholder="Comune *" className="col-span-4 w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base" />
              </div>
            )}
            {isN && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                {["nome", "cognome", "telefono"].map((f) => (
                  <input key={f} data-testid={`quick-notizia-${f}`} value={extra[f] || ""} onChange={(e) => setExtra({ ...extra, [f]: e.target.value })}
                    placeholder={PH[f]} className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm" />
                ))}
                <TipologiaSelect value={extra.tipologia} required={false} testid="quick-notizia-tipologia" onChange={(v) => setExtra({ ...extra, tipologia: v })} />
              </div>
            )}
            {isN && <LongTextarea data-testid="quick-notizia-testo" value={testo} onChange={(e) => setTesto(e.target.value)} rows={3}
              placeholder={cfg.placeholder} className="w-full mb-2 px-3.5 py-3 rounded-xl border border-[var(--border)] text-base focus:ring-2 focus:ring-[#FC82ED]/40 outline-none" />}
            {isN && <DuplicatiIndirizzo list={dupl} />}
            {isN && <button type="button" data-testid="quick-notizia-altri" onClick={() => setAltri((a) => !a)} className="text-xs font-semibold text-[#FC82ED] mb-2">{altri ? "− Nascondi altri dati" : "+ Altri dati"}</button>}
            {!isN && <>
            <LongTextarea data-testid={`quick-${kind}-testo`} value={testo} onChange={(e) => setTesto(e.target.value)} autoFocus={!isN} rows={isN ? 3 : 5}
              placeholder={cfg.placeholder}
              className="w-full px-3.5 py-3 rounded-xl border border-[var(--border)] text-base focus:ring-2 focus:ring-[#FC82ED]/40 outline-none" />
            <p className="text-[11px] text-[var(--muted)] mt-1.5 mb-3">Campi facoltativi (puoi completarli dopo):</p>
            <div className="grid grid-cols-2 gap-2">
              {cfg.extra.filter((f) => !(isN && f === "tipologia")).map((f) => (
                <input key={f} data-testid={`quick-${kind}-${f}`} value={extra[f] || ""} onChange={(e) => setExtra({ ...extra, [f]: e.target.value })}
                  placeholder={PH[f] || f}
                  className={`w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm ${!isN && f === "nome" ? "col-span-2" : ""}`} />
              ))}
            </div>
            </>}
            {isN && altri && (
              <div className="grid grid-cols-2 gap-2">
                {["email", "codice_fiscale"].map((f) => (
                  <input key={f} data-testid={`quick-notizia-${f}`} value={extra[f] || ""} onChange={(e) => setExtra({ ...extra, [f]: e.target.value })}
                    placeholder={PH[f]} className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm" />
                ))}
              </div>
            )}
            {isN && <div className="mt-3"><GiraAField value={gira.a} onChange={(a) => setGira({ ...gira, a })} note={gira.note} onNote={(note) => setGira({ ...gira, note })} testid="quick-notizia-gira" /></div>}
            <button data-testid={`quick-${kind}-salva`} onClick={salva} disabled={saving}
              className="w-full mt-4 py-3 rounded-xl bg-[#FC82ED] text-black font-bold text-sm disabled:opacity-50">
              {saving ? "Salvo…" : "Salva"}
            </button>
          </>
        ) : (
          <div data-testid={`quick-${kind}-proposta`}>
            <p className="text-sm text-[var(--muted)] mb-2">Ho letto il testo e propongo questi campi. Confermi?</p>
            <div className="space-y-1.5 mb-4">
              {cfg.show(proposta).map((line, i) => (
                <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-[#FC82ED]/5 border border-[#FC82ED]/20">
                  <Check size={14} className="text-[#FC82ED] shrink-0" /> {line}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button data-testid={`quick-${kind}-skip`} onClick={() => { onClose(); nav(cfg.detail(rec.id)); }}
                className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Salva senza</button>
              <button data-testid={`quick-${kind}-conferma`} onClick={conferma} disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white font-semibold text-sm disabled:opacity-50">Conferma e applica</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
